import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import {
  formatPaymentDetailsDate,
  formatPaymentDetailsDateWithTime,
} from "@src/appV2/redesign/Payments/PaymentDetails/utils/dates";
import { getExpectedPayoutDate } from "@src/appV2/redesign/Payments/utils/getExpectedPayoutDate";
import { Text } from "@src/appV2/redesign/system/ui-components";
import { parseISO } from "date-fns";

import { type ShiftPaymentStatus } from "../../api/useGetShiftPaymentStatus";

interface PaymentTimingRowProps {
  readonly paymentStatus: ShiftPaymentStatus | undefined;
  readonly isPaymentStatusLoading: boolean;
}

/**
 * Displays payment timing information based on payment status.
 * - For paid payments: Shows "Paid on [date]"
 * - For pending payments: Shows expected arrival time based on payout method
 * - For failed payments: No timing shown (handled by status pill)
 */
export function PaymentTimingRow({ paymentStatus, isPaymentStatusLoading }: PaymentTimingRowProps) {
  if (isPaymentStatusLoading || !isDefined(paymentStatus)) {
    return null;
  }

  const timing = getPaymentTimingText(paymentStatus);

  if (!isDefined(timing)) {
    return null;
  }

  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
        {timing.label}
      </Text>
      <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.primary })}>
        {timing.value}
      </Text>
    </Stack>
  );
}

interface PaymentTimingText {
  readonly label: string;
  readonly value: string;
}

/**
 * Returns the appropriate timing text based on payment status and method.
 * Uses the same date formatting and expected payout date logic as the Payment Details page.
 */
function getPaymentTimingText(paymentStatus: ShiftPaymentStatus): PaymentTimingText | undefined {
  const { payoutStatus, payoutMethod, createdAt } = paymentStatus;
  const isPayoutInstant = payoutMethod === "instant";

  // For paid payments, show the payment date with time (matching Payment Details page)
  if (payoutStatus === "paid" && isDefined(createdAt)) {
    const paymentDate = parseISO(createdAt);
    const formattedDate = formatPaymentDetailsDateWithTime(paymentDate);
    const methodLabel = isPayoutInstant ? " via InstantPay" : "";
    return {
      label: "Paid on",
      value: `${formattedDate}${methodLabel}`,
    };
  }

  // For pending payments, show expected delivery using getExpectedPayoutDate
  // (matching Payment Details page's PayoutDetailsSection)
  if (payoutStatus === "pending" && isDefined(createdAt)) {
    const paymentDate = parseISO(createdAt);
    const expectedDate = getExpectedPayoutDate({ paymentDate, isPayoutInstant });
    const formattedDate = isPayoutInstant
      ? formatPaymentDetailsDateWithTime(expectedDate)
      : formatPaymentDetailsDate(expectedDate);
    return {
      label: "Expected delivery",
      value: formattedDate,
    };
  }

  // For failed or undefined status, don't show timing
  return undefined;
}
